import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ListingTabs from '../../components/Listing/ListingTabs';
import SearchInput from '../../components/Listing/SearchInput';
import FilterDropdown from '../../components/Listing/FilterDropdown';
import { ICONS } from '../../constants/icons';
import { BLOGS } from './cityHubData';
import {
  VISIBLE_CITY_HUB_TABS,
  cityHubTabPath,
  getCityHubTabByPath,
} from './cityHubTabs';
import { CityHubAside, CityHubBanner, CityHubGenome } from './CityHubPanels';
import { pickDefaultCityHubCity } from './cityHubCity';

const FILTER_OPTIONS = [
  { value: 'music', label: 'Music' },
  { value: 'film', label: 'Film' },
  { value: 'visual-arts', label: 'Visual Arts' },
  { value: 'performing-arts', label: 'Performing Arts' },
  { value: 'literary', label: 'Literary' },
];

const blogTagClass = (category) =>
  `city-hub__blog-tag city-hub__blog-tag--${(category || '')
    .toLowerCase()
    .replace(/\s/g, '-')}`;

const CityHubBlogDetail = () => {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector(state => state.auth.currentUser);
  const cityId = pickDefaultCityHubCity(currentUser)?.id;
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState([]);

  document.title = 'Blog Detail | City Hub | AMOK . EARTH';

  const blog = useMemo(
    () => BLOGS.find((item) => item.id === Number(blogId)) || BLOGS[0],
    [blogId]
  );

  const tabItems = useMemo(
    () =>
      VISIBLE_CITY_HUB_TABS.map((tab) => ({
        key: tab.path,
        label: tab.label,
        icon: tab.icon,
      })),
    []
  );

  const handleTabChange = (key) => {
    const nextTab = getCityHubTabByPath(key);
    if (nextTab) navigate(cityHubTabPath(nextTab, cityId));
  };

  const authorRole = blog.author?.role || 'Arts & Culture Correspondent';
  const sections = blog.content || [];

  return (
    <div className="page-content">
      <div className="city-hub city-hub--detail">
        <CityHubBanner />
        <CityHubGenome />

        <div className="city-hub__main">
          <div className="city-hub__toolbar">
            <ListingTabs
              items={tabItems}
              activeKey="blogs"
              onChange={handleTabChange}
            />
            <div className="city-hub__toolbar-actions">
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by Title..."
                iconSrc={ICONS.search}
              />
              <FilterDropdown
                options={FILTER_OPTIONS}
                selected={filters}
                onChange={setFilters}
              />
            </div>
          </div>

          <article className="city-hub-blog">
            <header className="city-hub-blog__hero">
              <div className="city-hub-blog__hero-main">
                <div className="city-hub-blog__meta">
                  <span className={blogTagClass(blog.category)}>{blog.category}</span>
                  <span className="city-hub-blog__meta-item">{blog.readTime}</span>
                  <span className="city-hub-blog__meta-item">
                    Published: {blog.published}
                  </span>
                </div>
                <h1 className="city-hub-blog__title">{blog.title}</h1>
                <div className="city-hub-blog__author">
                  <img
                    src={blog.author.avatar}
                    alt=""
                    width={32}
                    height={32}
                  />
                  <div className="city-hub-blog__author-info">
                    <div className="city-hub-blog__author-name">{blog.author.name}</div>
                    <div className="city-hub-blog__author-role">{authorRole}</div>
                  </div>
                </div>
              </div>
              <div className="city-hub-blog__image">
                <img src={blog.image} alt="" />
              </div>
            </header>

            <div className="city-hub-blog__divider" aria-hidden="true" />

            <div className="city-hub-blog__article">
              {blog.intro && <p className="city-hub-blog__intro">{blog.intro}</p>}

              {sections.map((section, index) => {
                const HeadingTag = section.level === 'h2' ? 'h2' : 'h3';
                return (
                  <React.Fragment key={`${section.heading || 'block'}-${index}`}>
                    {section.heading && (
                      <HeadingTag
                        className={`city-hub-blog__section-title ${
                          section.level === 'h2'
                            ? 'city-hub-blog__section-title--lg'
                            : ''
                        }`}
                      >
                        {section.heading}
                      </HeadingTag>
                    )}

                    {section.showQuoteAfter && blog.quote && (
                      <blockquote className="city-hub-blog__quote">
                        <div className="city-hub-blog__quote-accent" aria-hidden="true" />
                        <div className="city-hub-blog__quote-body">
                          <p className="city-hub-blog__quote-text">
                            &ldquo;{blog.quote.text}&rdquo;
                          </p>
                          <cite className="city-hub-blog__quote-attr">
                            - {blog.quote.attribution}
                          </cite>
                        </div>
                      </blockquote>
                    )}

                    {section.text && (
                      <p className="city-hub-blog__section-text">{section.text}</p>
                    )}
                  </React.Fragment>
                );
              })}

              {!blog.intro && sections.length === 0 && (
                <p className="city-hub-blog__section-text">{blog.excerpt}</p>
              )}

              {blog.quote && !sections.some((section) => section.showQuoteAfter) && (
                <blockquote className="city-hub-blog__quote">
                  <div className="city-hub-blog__quote-accent" aria-hidden="true" />
                  <div className="city-hub-blog__quote-body">
                    <p className="city-hub-blog__quote-text">
                      &ldquo;{blog.quote.text}&rdquo;
                    </p>
                    <cite className="city-hub-blog__quote-attr">
                      - {blog.quote.attribution}
                    </cite>
                  </div>
                </blockquote>
              )}
            </div>

            {blog.tags?.length > 0 && (
              <div className="city-hub-blog__tags">
                <span className="city-hub-blog__tags-label">Tags:</span>
                {blog.tags.map((tag) => (
                  <span key={tag} className="city-hub-blog__chip">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="city-hub-blog__divider" aria-hidden="true" />

            <div className="city-hub-blog__engagement">
              <button type="button" className="city-hub-blog__engage">
                <img src={ICONS.heart} alt="" width={16} height={16} />
                <span>{blog.likes || 0} Likes</span>
              </button>
              <button type="button" className="city-hub-blog__engage">
                <img src={ICONS.messageText} alt="" width={16} height={16} />
                <span>{blog.comments || 0} Comments</span>
              </button>
              <button
                type="button"
                className="city-hub-blog__engage city-hub-blog__engage--share"
              >
                <img src={ICONS.messageShare} alt="" width={16} height={16} />
                <span>Share</span>
              </button>
            </div>
          </article>
        </div>

        <CityHubAside />
      </div>
    </div>
  );
};

export default CityHubBlogDetail;
